import { syncRefs, useAsyncState, useLocalStorage } from "@vueuse/core";
import { ref, toValue } from "vue";
import { useRouter } from "vue-router";
import { useGetMeQuery, useLoadCartAfterFirstTimeSignInMutation, useMergeCartMutation } from "@/core/api/graphql";
import { updateLanguageAndCurrency } from "@/core/api/graphql/account/mutations/updateLanguageAndCurrency";
import { useAuth } from "@/core/composables/useAuth";
import { useCurrency } from "@/core/composables/useCurrency";
import { useLanguages } from "@/core/composables/useLanguages";
import { USER_ID_LOCAL_STORAGE } from "@/core/constants/local-storage-keys";
import { NON_EMPTY_CARTS_MERGED_SESSION_STORAGE } from "@/core/constants/session-storage-keys";
import { Logger } from "@/core/utilities";
import { TabsType, openReturnUrl, useBroadcast } from "@/shared/broadcast";
import { useFullCart } from "@/shared/cart/composables";
import { useWishlists } from "@/shared/wishlists";
import type {
  ContactTypeExtension,
  IdentityErrorType,
  InputUpdateLanguageAndCurrencyType,
} from "@/core/api/graphql/types";
import type { SignMeIn } from "@/shared/account/types";
import type { MaybeRefOrGetter } from "vue";

export function useSignMeIn(payload: MaybeRefOrGetter<SignMeIn>) {
  const { errors: authErrors, authorize } = useAuth();
  const broadcast = useBroadcast();
  const { cart, saveCartOnActionURL } = useFullCart();
  const { moveFromItemsForLaterToCart } = useWishlists();
  const { mutate: mergeCart } = useMergeCartMutation();
  const { mutate: loadCartAfterFirstTimeSignIn } = useLoadCartAfterFirstTimeSignInMutation();
  const { supportedLanguages, saveLocale, currentLanguage } = useLanguages();
  const { supportedCurrencies, saveCurrencyCode, currentCurrency } = useCurrency();
  const { result: me, load: getMe } = useGetMeQuery();
  const savedLocale = useLocalStorage<string>("locale", "");
  const savedCurrencyCode = useLocalStorage<string | null>("currency", "");
  const router = useRouter();

  const { isLoading: loading, execute: signIn } = useAsyncState(
    async () => {
      const { email, password } = toValue(payload);
      await authorize(email, password, currentLanguage.value.cultureName, currentCurrency.value.code);
      localStorage.removeItem(USER_ID_LOCAL_STORAGE);

      // get user that will be applied after reload.
      await getMe();

      if (me.value?.me) {
        const meCopy = me.value.me;

        if (router.currentRoute.value.query.action === "save-cart" && !router.currentRoute.value.query.callbackAction) {
          await saveCartOnActionURL(meCopy.id, cart.value?.id);
        }

        if (router.currentRoute.value.query.action === "save-items") {
          const item = router.currentRoute.value.query.optionalItem
            ? router.currentRoute.value.query.optionalItem.toString()
            : "";
          await moveFromItemsForLaterToCart([item], me.value?.me?.id, cart.value?.id);
        }

        if (meCopy.contact?.status === "Imported") {
          await loadCartAfterFirstTimeSignIn({ command: { userId: meCopy.id } });
        }

        // Always call mergeCart, even without a guest cart: the server converts the user's legacy BRL carts to USD here.
        const guestCartId = cart.value?.id;

        try {
          const mergeResult = await mergeCart({ command: { userId: meCopy.id, secondCartId: guestCartId ?? "" } });

          // Check if nonEmptyCartsMergedTL property is true and store flag in session storage
          if (mergeResult?.data?.mergeCart?.nonEmptyCartsMergedTL) {
            sessionStorage.setItem(NON_EMPTY_CARTS_MERGED_SESSION_STORAGE, "true");
          }
        } catch (error) {
          if (guestCartId) {
            throw error;
          }

          Logger.error("Failed to convert user carts during sign-in", error);
        }

        let isUpdateContactLanguageNeeded = false;
        let isUpdateContactCurrencyNeeded = false;

        if (meCopy.contact?.defaultLanguage) {
          const contactLanguage = supportedLanguages.value.find(
            (item) => item.cultureName === meCopy.contact!.defaultLanguage,
          );

          if (contactLanguage) {
            saveLocale(contactLanguage.twoLetterLanguageName, false);
          }
        } else if (savedLocale.value) {
          isUpdateContactLanguageNeeded = true;
        }

        if (meCopy.contact!.currencyCode) {
          const contactCurrency = supportedCurrencies.value.find((item) => item.code === meCopy.contact!.currencyCode);

          if (contactCurrency) {
            saveCurrencyCode(contactCurrency.code, false);
          }
        } else if (savedCurrencyCode.value) {
          isUpdateContactCurrencyNeeded = true;
        }

        if (isUpdateContactLanguageNeeded || isUpdateContactCurrencyNeeded) {
          await updateLanguageCurrency(
            isUpdateContactCurrencyNeeded,
            isUpdateContactLanguageNeeded,
            meCopy.contact as ContactTypeExtension,
          );
        }
      }

      void broadcast.emit(openReturnUrl, undefined, TabsType.ALL);
    },
    null,
    { immediate: false },
  );

  async function updateLanguageCurrency(
    isUpdateContactCurrencyNeeded: boolean,
    isUpdateContactLanguageNeeded: boolean,
    contact: ContactTypeExtension,
  ) {
    const contactData: InputUpdateLanguageAndCurrencyType = {
      language: isUpdateContactLanguageNeeded
        ? supportedLanguages.value.find((lang) => lang.twoLetterLanguageName === savedLocale.value)?.cultureName
        : contact?.defaultLanguage,
      currency: isUpdateContactCurrencyNeeded
        ? supportedCurrencies.value.find((currency) => currency.code === savedCurrencyCode.value)?.code
        : contact?.currencyCode,
      userId: contact?.id ?? "",
    };

    await updateLanguageAndCurrency(contactData);
  }
  const errors = ref<IdentityErrorType[]>();

  syncRefs(authErrors, errors);

  function resetErrors() {
    errors.value = [];
  }

  return {
    errors,
    loading,
    signIn,
    resetErrors,
    authorize,
  };
}
